import type { RefObject } from "react";

import type { AdminUser } from "@/lib/auth-api";
import type { NavKey } from "@/lib/qa-types";

const navItems: { key: NavKey; label: string; glyph: string }[] = [
    { key: "browser", label: "质量探索", glyph: "◎" },
    { key: "generate", label: "场景生成", glyph: "✦" },
    { key: "runs", label: "任务执行", glyph: "▶" },
    { key: "overview", label: "质量总览", glyph: "⌁" },
    { key: "agents", label: "Agent 资源池", glyph: "▦" },
    { key: "orders", label: "订单中心", glyph: "▤" },
];

export type AnalysisSaveState = "idle" | "saving" | "saved" | "error";

export function workspaceLabel(active: NavKey): string | undefined {
    if (active === "defects") return "缺陷中心";
    return active === "integrations" ? "集成与模型" : navItems.find((item) => item.key === active)?.label;
}

export function AppSidebar({
    active,
    currentUser,
    executionCount,
    onLogout,
    onNavigate,
}: {
    active: NavKey;
    currentUser: AdminUser;
    executionCount: number;
    onLogout: () => void;
    onNavigate: (target: NavKey) => void;
}) {
    const initials = currentUser.display_name.slice(0, 2).toUpperCase();
    return (
        <aside className="sidebar">
            <div className="brand">
                <div className="brand-mark">Q</div>
                <div>
                    <strong>QAScope</strong>
                    <span>Agent Platform</span>
                    <small className="company-name">点泽智能科技</small>
                </div>
            </div>
            <div className="project-switcher">
                <span className="project-logo">S</span>
                <div>
                    <small>当前项目</small>
                    <strong>智测商城 · UAT</strong>
                </div>
                <button aria-label="切换项目">⌄</button>
            </div>
            <nav aria-label="主导航">
                <p>工作台</p>
                {navItems
                    .filter((item) => item.key !== "browser" || currentUser.roles.includes("ADMIN"))
                    .map((item) => (
                        <button className={active === item.key ? "active" : ""} key={item.key} onClick={() => onNavigate(item.key)}>
                            <span>{item.glyph}</span>
                            {item.label}
                            {item.key === "runs" && <i>7</i>}
                        </button>
                    ))}
                <p>质量闭环</p>
                <button className={active === "defects" ? "active" : ""} onClick={() => onNavigate("defects")}>
                    <span>◫</span>缺陷中心{executionCount > 0 && <i className="warning-count">{executionCount}</i>}
                </button>
                <button>
                    <span>⌁</span>质量洞察
                </button>
                <p>平台管理</p>
                {currentUser.roles.includes("ADMIN") && (
                    <button className={active === "integrations" ? "active" : ""} onClick={() => onNavigate("integrations")}>
                        <span>◇</span>集成与模型
                    </button>
                )}
                <button>
                    <span>⚙</span>平台设置
                </button>
            </nav>
            <div className="sidebar-foot">
                <div className="avatar">{initials}</div>
                <div>
                    <strong>{currentUser.display_name}</strong>
                    <span>{currentUser.roles.join(" / ")}</span>
                </div>
                <button aria-label="退出登录" onClick={onLogout}>
                    退出
                </button>
            </div>
        </aside>
    );
}

export function AppTopbar({
    accountMenuOpen,
    accountMenuRef,
    activeLabel,
    currentUser,
    dataSource,
    onLogout,
    onToggleAccountMenu,
    saveState,
    showSaveState,
}: {
    accountMenuOpen: boolean;
    accountMenuRef: RefObject<HTMLDivElement | null>;
    activeLabel: string | undefined;
    currentUser: AdminUser;
    dataSource: "live" | "demo";
    onLogout: () => void;
    onToggleAccountMenu: () => void;
    saveState: AnalysisSaveState;
    showSaveState: boolean;
}) {
    const initials = currentUser.display_name.slice(0, 2).toUpperCase();
    const saveLabel = {
        idle: "○ 待保存",
        saving: "○ 保存中",
        saved: "✓ 已保存",
        error: "! 未保存",
    }[saveState];
    return (
        <header className="topbar">
            <div className="breadcrumb">
                <span>智测商城</span>
                <b>/</b>
                <strong>{activeLabel}</strong>
            </div>
            <div className="top-actions">
                <span className={`data-source ${dataSource}`}>● {dataSource === "live" ? "后端已连接" : "演示数据"}</span>
                {showSaveState && <span className={`analysis-save-state ${saveState}`}>{saveLabel}</span>}
                <button aria-label="搜索">⌕</button>
                <button className="notification" aria-label="通知">
                    ♢<i />
                </button>
                <div className="account-menu" ref={accountMenuRef}>
                    <button
                        aria-expanded={accountMenuOpen}
                        aria-haspopup="menu"
                        aria-label="打开账户菜单"
                        className="avatar compact account-menu-trigger"
                        onClick={onToggleAccountMenu}
                        title={currentUser.username}
                    >
                        {initials}
                    </button>
                    {accountMenuOpen && (
                        <div aria-label="账户菜单" className="account-menu-popover" role="menu">
                            <div className="account-menu-user">
                                <strong>{currentUser.display_name}</strong>
                                <span>{currentUser.username}</span>
                                <small>{currentUser.roles.join(" / ")}</small>
                            </div>
                            <button className="account-menu-logout" onClick={onLogout} role="menuitem">
                                <span aria-hidden="true">↪</span>
                                退出登录
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </header>
    );
}
